/**
 * Options for generating the API key signature
 */
export interface IApiKeySignatureOptions {
  httpMethod: string;
  requestPath: string;
  queryString?: string;
  body?: unknown;
  nonce: string;
  timestamp: number;
  secret: string;
  shouldIncludeBodyOrQuery?: boolean;
}

/**
 * Normalize body for consistent hashing
 */
function normalizeBody(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== 'object') {
    return {};
  }

  try {
    // Clone by serializing/deserializing
    const cloned = JSON.parse(JSON.stringify(body));
    const normalized: Record<string, unknown> = {};

    Object.entries(cloned).forEach(([key, value]) => {
      // Skip nullish values
      if (value === null || value === undefined) {
        return;
      }

      // Skip arrays and objects
      if (typeof value === 'object') {
        return;
      }

      normalized[key] = value;
    });

    // Sort by keys
    const sorted: Record<string, unknown> = {};
    Object.keys(normalized)
      .sort()
      .forEach((key) => {
        sorted[key] = normalized[key];
      });

    return sorted;
  } catch {
    return {};
  }
}

/**
 * Generate SHA256 hash of request body
 */
export async function generateBodySha256(body: unknown): Promise<string> {
  if (!body || typeof body !== 'object') {
    return '';
  }

  const normalized = normalizeBody(body);

  if (Object.keys(normalized).length === 0) {
    return '';
  }

  const bodyString = JSON.stringify(normalized);
  const encoder = new TextEncoder();
  const data = encoder.encode(bodyString);

  try {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Failed to generate body SHA256:', error);
    throw new Error('Failed to generate body hash');
  }
}

export function buildStringToSign(options: {
  httpMethod: string;
  requestPath: string;
  queryString: string;
  bodySha256: string;
  nonce: string;
  timestamp: number;
}): string {
  const { httpMethod, requestPath, queryString, bodySha256, nonce, timestamp } = options;

  return [
    httpMethod.toUpperCase(),
    requestPath,
    queryString,
    bodySha256,
    nonce,
    timestamp.toString(),
  ].join('\n');
}

/**
 * Generate HMAC-SHA256 signature
 */
export async function generateHmacSignature(stringToSign: string, secret: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const secretData = encoder.encode(secret);
    const messageData = encoder.encode(stringToSign);

    // Import the secret as a key
    const key = await crypto.subtle.importKey(
      'raw',
      secretData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    // Sign the message
    const signature = await crypto.subtle.sign('HMAC', key, messageData);

    // Convert to Base64 using modern approach
    const signatureBytes = new Uint8Array(signature);
    const binaryArray = Array.from(signatureBytes);
    let binary = '';
    for (const byte of binaryArray) {
      binary += String.fromCharCode(byte);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - btoa is available in browsers, just deprecated in TS
    return btoa(binary);
  } catch (error) {
    console.error('Failed to generate HMAC signature:', error);
    throw new Error('Failed to generate HMAC signature');
  }
}

export async function generateApiKeySignature(options: IApiKeySignatureOptions): Promise<string> {
  const {
    httpMethod,
    requestPath = '',
    queryString = '',
    body,
    nonce,
    timestamp,
    secret,
    shouldIncludeBodyOrQuery,
  } = options;

  try {
    // Generate body hash if body should be included
    const bodySha256 = shouldIncludeBodyOrQuery && body ? await generateBodySha256(body) : '';
    const finalQueryString = shouldIncludeBodyOrQuery ? queryString : '';

    const stringToSign = buildStringToSign({
      httpMethod,
      requestPath,
      queryString: finalQueryString,
      bodySha256,
      nonce,
      timestamp,
    });

    return await generateHmacSignature(stringToSign, secret);
  } catch (error) {
    console.error('Failed to generate API key signature:', error);
    throw error;
  }
}

export function generateNonce(): string {
  return crypto.randomUUID();
}

/**
 * Encode nonce, timestamp, and signature into a single base64-encoded x-api-key
 * Format: base64(JSON.stringify({ nonce, timestamp, signature }))
 */
export function encodeApiKey(nonce: string, timestamp: number, signature: string): string {
  const apiKeyData = {
    nonce,
    timestamp,
    signature,
  };

  const jsonString = JSON.stringify(apiKeyData);
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(jsonString);
  const binaryArray = Array.from(encodedData);
  let binary = '';
  for (const byte of binaryArray) {
    binary += String.fromCharCode(byte);
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - btoa is available in browsers, just deprecated in TS
  return btoa(binary);
}

/**
 * Decode x-api-key to extract nonce, timestamp, and signature
 */
export function decodeApiKey(
  apiKey: string,
): { nonce: string; timestamp: number; signature: string } | null {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - atob is available in browsers, just deprecated in TS
    const binary = atob(apiKey);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const decoder = new TextDecoder();
    const jsonString = decoder.decode(bytes);
    const data = JSON.parse(jsonString);

    if (data.nonce && typeof data.timestamp === 'number' && data.signature) {
      return {
        nonce: data.nonce,
        timestamp: data.timestamp,
        signature: data.signature,
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to decode API key:', error);
    return null;
  }
}
